import React from 'react';

export type AnnouncementBarTone = 'contrast' | 'accent' | 'dark';

export interface AnnouncementBarAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface AnnouncementBarProps {
  /** @default "contrast" */
  tone?: AnnouncementBarTone;
  /** Small uppercase chip before the message, e.g. "New". */
  tag?: string;
  /** Inline CTA link rendered after the message. */
  action?: AnnouncementBarAction;
  /** Show a dismiss × and handle click. */
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const BACKGROUNDS: Record<AnnouncementBarTone, string> = {
  contrast: 'linear-gradient(135deg, #111827 0%, #030712 100%)',
  accent:   'linear-gradient(135deg, #19a75c 0%, #168a4d 100%)',
  dark:     'linear-gradient(150deg, #174530 0%, #0a2619 100%)',
};

const CHIP: Record<AnnouncementBarTone, { bg: string; fg: string }> = {
  contrast: { bg: '#19a75c', fg: '#fff' },
  accent:   { bg: 'rgba(255,255,255,0.18)', fg: '#fff' },
  dark:     { bg: 'rgba(255,255,255,0.16)', fg: '#bbe9d0' },
};

const LINK_FG: Record<AnnouncementBarTone, string> = {
  contrast: '#88d7af',
  accent:   '#fff',
  dark:     '#bbe9d0',
};

const LINK_BORDER: Record<AnnouncementBarTone, string> = {
  contrast: 'rgba(120,215,175,0.5)',
  accent:   'rgba(255,255,255,0.45)',
  dark:     'rgba(187,233,208,0.45)',
};

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const CloseIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const AnnouncementBar: React.FC<AnnouncementBarProps> = ({
  tone = 'contrast',
  tag,
  action,
  onClose,
  className = '',
  style = {},
  children,
}) => {
  const chip = CHIP[tone];
  const linkFg = LINK_FG[tone];
  const linkBorder = LINK_BORDER[tone];

  return (
    <div
      role="region"
      aria-label="Announcement"
      className={className}
      style={{
        position: 'relative', width: '100%', boxSizing: 'border-box',
        background: BACKGROUNDS[tone], color: '#fff',
        fontFamily: '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif',
        ...style,
      }}
    >
      <div style={{
        maxWidth: 1120, margin: '0 auto', minHeight: 44,
        padding: onClose ? '0 48px' : '0 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
      }}>
        <p style={{
          margin: 0, padding: '7px 0',
          display: 'inline-flex', alignItems: 'center', gap: 9,
          flexWrap: 'wrap', justifyContent: 'center', lineHeight: 1.4,
          fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.96)',
        }}>
          {tag && (
            <span style={{
              fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '3px 9px', borderRadius: 9999, background: chip.bg, color: chip.fg,
            }}>{tag}</span>
          )}
          <span>{children}</span>
          {action && (
            <a
              href={action.href}
              onClick={action.onClick}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontWeight: 700, color: linkFg, whiteSpace: 'nowrap',
                textDecoration: 'none', borderBottom: `1px solid ${linkBorder}`,
                paddingBottom: 1, cursor: 'pointer',
              }}
            >
              {action.label} <ArrowIcon />
            </a>
          )}
        </p>
      </div>
      {onClose && (
        <button
          type="button"
          aria-label="Dismiss announcement"
          onClick={onClose}
          style={{
            position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 30, height: 30, border: 'none', background: 'transparent',
            color: 'rgba(255,255,255,0.8)', cursor: 'pointer', borderRadius: 8, padding: 0,
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

export default AnnouncementBar;
